import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import './Admin.css';
import { getLabeledVideoUrl } from '../lib/videoUtils';
import { deleteVideoRecord } from '../lib/deleteVideo';
import BodyPatternTicks from '../components/BodyPatternTicks';

const LIMB_OPTIONS = [
  'Left Front', 'Right Front', 'Left Hind', 'Right Hind',
  'Both Front', 'Both Hind', 'All Limbs', 'Back/Pelvis'
];

const Admin = ({ user }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [labels, setLabels] = useState({});
  const [selectedUser, setSelectedUser] = useState('all');
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [tab, setTab] = useState('videos');
  const [labelFilter, setLabelFilter] = useState('all'); // all | unlabeled | correct | incorrect
  const [savingLabel, setSavingLabel] = useState(null);
  const [toast, setToast] = useState(null);
  const [deletingVideo, setDeletingVideo] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);

  // Label form state
  const [labelForm, setLabelForm] = useState({});

  useEffect(() => {
    if (user) checkAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const checkAdmin = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', user.id)
      .single();

    const knownAdmin = (user.email || '').toLowerCase() === 'jphorselver@gmail.com';
    if (data?.is_admin || knownAdmin) {
      setIsAdmin(true);
      await loadData();
    }
    setLoading(false);
  };

  const loadData = async () => {
    const [usersRes, videosRes, labelsRes] = await Promise.all([
      supabase
        .from('profiles')
        .select('id, email, full_name, created_at, subscription_tier')
        .order('created_at', { ascending: false }),
      supabase
        .from('videos')
        .select('*, analysis_reports(*), horses(name), profiles!videos_owner_id_fkey(email, full_name)')
        .order('upload_date', { ascending: false }),
      supabase
        .from('training_labels')
        .select('*')
        .order('created_at', { ascending: false }),
    ]);
    if (usersRes.error || videosRes.error || labelsRes.error) {
      console.error('Admin load failed', usersRes.error, videosRes.error, labelsRes.error);
      showToast(videosRes.error?.message || usersRes.error?.message || labelsRes.error?.message || 'Could not load admin data.');
    }
    setUsers(usersRes.data || []);
    setVideos(videosRes.data || []);

    // Index labels by video_id
    const labelsMap = {};
    (labelsRes.data || []).forEach(l => { labelsMap[l.video_id] = l; });
    setLabels(labelsMap);
  };

  const getFilteredVideos = () => {
    let filtered = selectedUser === 'all'
      ? videos
      : videos.filter(v => v.owner_id === selectedUser);

    if (labelFilter === 'unlabeled') filtered = filtered.filter(v => !labels[v.id]);
    else if (labelFilter === 'correct') filtered = filtered.filter(v => labels[v.id]?.is_correct === true);
    else if (labelFilter === 'incorrect') filtered = filtered.filter(v => labels[v.id]?.is_correct === false);

    return filtered;
  };

  const filteredVideos = getFilteredVideos();

  const initLabelForm = (videoId, report) => {
    const existing = labels[videoId];
    if (existing) {
      setLabelForm({
        [videoId]: {
          is_correct: existing.is_correct,
          correct_result: existing.correct_result || (report?.is_abnormal ? 'abnormal' : 'normal'),
          correct_severity: existing.correct_severity || report?.overall_severity || 'normal',
          affected_limbs: existing.affected_limbs || [],
          notes: existing.notes || '',
        }
      });
    } else {
      setLabelForm({
        [videoId]: {
          is_correct: null,
          correct_result: report?.is_abnormal ? 'abnormal' : 'normal',
          correct_severity: report?.overall_severity || 'normal',
          affected_limbs: [],
          notes: '',
        }
      });
    }
  };

  const updateLabelField = (videoId, field, value) => {
    setLabelForm(prev => ({
      ...prev,
      [videoId]: { ...prev[videoId], [field]: value }
    }));
  };

  const toggleLimb = (videoId, limb) => {
    const current = labelForm[videoId]?.affected_limbs || [];
    const updated = current.includes(limb)
      ? current.filter(l => l !== limb)
      : [...current, limb];
    updateLabelField(videoId, 'affected_limbs', updated);
  };

  const saveLabel = async (videoId) => {
    const form = labelForm[videoId];
    if (form.is_correct === null) return;

    setSavingLabel(videoId);
    const existing = labels[videoId];

    const labelData = {
      video_id: videoId,
      reviewer_id: user.id,
      is_correct: form.is_correct,
      correct_result: form.is_correct ? null : form.correct_result,
      correct_severity: form.is_correct ? null : form.correct_severity,
      affected_limbs: form.is_correct ? null : form.affected_limbs,
      notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };

    let error;
    if (existing) {
      ({ error } = await supabase
        .from('training_labels')
        .update(labelData)
        .eq('id', existing.id));
    } else {
      ({ error } = await supabase
        .from('training_labels')
        .insert(labelData));
    }

    if (error) {
      console.error('Error saving label:', error);
      showToast('❌ Error saving label');
    } else {
      showToast('✅ Label saved!');
      await loadData();
    }
    setSavingLabel(null);
  };

  const deleteVideo = async (video) => {
    if (!window.confirm(`Delete ${video.filename}? This cannot be undone.`)) return;
    setDeletingVideo(video.id);
    try {
      await deleteVideoRecord(supabase, video);
      setVideos((prev) => prev.filter((v) => v.id !== video.id));
      setLabels((prev) => {
        const next = { ...prev };
        delete next[video.id];
        return next;
      });
      showToast(`Deleted ${video.filename}`);
      await loadData();
    } catch (err) {
      console.error('Delete error:', err);
      showToast(`Could not delete: ${err.message}`);
    }
    setDeletingVideo(null);
  };

  const deleteUserProfile = async (profile) => {
    if (profile.id === user.id) {
      showToast('You cannot delete your own admin account from here.');
      return;
    }
    if (confirmDeleteUser !== profile.id) {
      setConfirmDeleteUser(profile.id);
      return;
    }
    setDeletingUser(profile.id);
    setConfirmDeleteUser(null);
    try {
      const owned = videos.filter(v => v.owner_id === profile.id);
      for (const video of owned) {
        await deleteVideoRecord(supabase, video);
      }
      await supabase.from('user_feedback').delete().eq('user_id', profile.id);
      await supabase.from('horses').delete().eq('owner_id', profile.id);
      const { error } = await supabase.from('profiles').delete().eq('id', profile.id);
      if (error) throw error;
      showToast(`Deleted ${profile.full_name || profile.email}`);
      await loadData();
    } catch (err) {
      console.error('Delete user error:', err);
      showToast(`Could not delete user: ${err.message}`);
    }
    setDeletingUser(null);
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#22c55e';
    if (score >= 80) return '#eab308';
    if (score >= 70) return '#f97316';
    return '#ef4444';
  };

  const labeledCount = Object.keys(labels).length;
  const incorrectCount = Object.values(labels).filter(l => !l.is_correct).length;

  if (loading) {
    return <div className="admin-container"><div className="admin-loading">Loading...</div></div>;
  }

  if (!isAdmin) {
    return (
      <div className="admin-container">
        <div className="admin-denied">
          <h2>🔒 Admin Access Required</h2>
          <p>You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {toast && <div className="admin-toast">{toast}</div>}

      <div className="admin-header">
        <h1>🔧 Admin Dashboard</h1>
        <p>{users.length} users • {videos.length} videos • {labeledCount} labeled • {incorrectCount} corrections</p>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button className={`tab-btn ${tab === 'videos' ? 'active' : ''}`} onClick={() => setTab('videos')}>
          📹 Videos ({videos.length})
        </button>
        <button className={`tab-btn ${tab === 'label' ? 'active' : ''}`} onClick={() => setTab('label')}>
          🏷️ Label & Review ({videos.length - labeledCount} remaining)
        </button>
        <button className={`tab-btn ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          👥 Users ({users.length})
        </button>
        <button className={`tab-btn ${tab === 'stats' ? 'active' : ''}`} onClick={() => setTab('stats')}>
          📊 Training Stats
        </button>
      </div>

      {/* Videos Tab */}
      {tab === 'videos' && (
        <>
          <div className="filter-bar">
            <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
              <option value="all">All Users ({videos.length})</option>
              {users.map(u => {
                const count = videos.filter(v => v.owner_id === u.id).length;
                if (count === 0) return null;
                return <option key={u.id} value={u.id}>{u.full_name || u.email} ({count})</option>;
              })}
            </select>
          </div>

          <div className="video-list">
            {filteredVideos.map(video => {
              const report = video.analysis_reports?.[0];
              const isExpanded = expandedVideo === video.id;
              const ownerName = video.profiles?.full_name || video.profiles?.email || 'Unknown';
              const label = labels[video.id];

              return (
                <div key={video.id} className="video-item">
                  <div className="video-item-main" onClick={() => setExpandedVideo(isExpanded ? null : video.id)}>
                    <div className="video-icon">🎬</div>
                    <div className="video-details">
                      <h4>{video.filename}</h4>
                      <p className="video-meta">
                        👤 {ownerName}
                        {video.horses?.name && <> • 🐴 {video.horses.name}</>}
                        {' • '}{formatDate(video.upload_date)}
                        {video.file_size_bytes ? ` • ${formatSize(video.file_size_bytes)}` : ''}
                      </p>
                    </div>
                    <div className="video-badges">
                      {label && (
                        <span className={`label-badge ${label.is_correct ? 'correct' : 'incorrect'}`}>
                          {label.is_correct ? '✅' : '❌'}
                        </span>
                      )}
                      {report ? (
                        <span className="score-pill" style={{ backgroundColor: getScoreColor(report.soundness_score) }}>
                          {report.soundness_score}/100
                        </span>
                      ) : (
                        <span className="status-pill pending">⏳</span>
                      )}
                    </div>
                    <span className={`expand-arrow ${isExpanded ? 'expanded' : ''}`}>▸</span>
                  </div>

                  {isExpanded && (
                    <div className="video-expanded">
                      {report && (
                        <div className="report-summary">
                          <div className="report-row">
                            <span className="report-label">AI Assessment</span>
                            <span className="report-value">{report.is_abnormal ? '⚠️ Abnormal' : '✅ Normal'}</span>
                          </div>
                          <div className="report-row">
                            <span className="report-label">Score</span>
                            <span className="report-value" style={{ color: getScoreColor(report.soundness_score) }}>{report.soundness_score}/100</span>
                          </div>
                          <div className="report-row">
                            <span className="report-label">Severity</span>
                            <span className="report-value">{report.overall_severity}</span>
                          </div>
                        </div>
                      )}
                      {report?.findings?.length > 0 && (
                        <div className="findings-list">
                          <h5>Findings</h5>
                          {report.findings.map((f, i) => (
                            <div key={i} className="finding-item">
                              <strong>{f.title}</strong>
                              <p>{f.description}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      <BodyPatternTicks videoId={video.id} user={user} />
                      <div className="video-actions">
                        <a href={video.file_url} target="_blank" rel="noopener noreferrer" className="action-link">▶️ Watch Video</a>
                        <button className="action-link" onClick={() => { setTab('label'); setExpandedVideo(video.id); initLabelForm(video.id, report); }}>
                          🏷️ Label This Video
                        </button>
                        <button
                          className="action-link delete-btn"
                          onClick={(e) => { e.stopPropagation(); deleteVideo(video); }}
                          disabled={deletingVideo === video.id}
                        >
                          {deletingVideo === video.id ? 'Deleting…' : 'Delete Video'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Label & Review Tab */}
      {tab === 'label' && (
        <>
          <div className="filter-bar">
            <div className="label-filters">
              {['all', 'unlabeled', 'correct', 'incorrect'].map(f => (
                <button key={f} className={`filter-chip ${labelFilter === f ? 'active' : ''}`} onClick={() => setLabelFilter(f)}>
                  {f === 'all' && `All (${videos.length})`}
                  {f === 'unlabeled' && `⬜ Unlabeled (${videos.length - labeledCount})`}
                  {f === 'correct' && `✅ Correct (${labeledCount - incorrectCount})`}
                  {f === 'incorrect' && `❌ Incorrect (${incorrectCount})`}
                </button>
              ))}
            </div>
          </div>

          <div className="video-list">
            {filteredVideos.map(video => {
              const report = video.analysis_reports?.[0];
              const isExpanded = expandedVideo === video.id;
              const ownerName = video.profiles?.full_name || video.profiles?.email || 'Unknown';
              const label = labels[video.id];
              const form = labelForm[video.id];

              return (
                <div key={video.id} className={`video-item ${label ? (label.is_correct ? 'labeled-correct' : 'labeled-incorrect') : 'unlabeled'}`}>
                  <div className="video-item-main" onClick={() => {
                    const newExpanded = isExpanded ? null : video.id;
                    setExpandedVideo(newExpanded);
                    if (newExpanded) initLabelForm(video.id, report);
                  }}>
                    <div className="video-icon">
                      {label ? (label.is_correct ? '✅' : '❌') : '⬜'}
                    </div>
                    <div className="video-details">
                      <h4>{video.horses?.name || 'No horse'} — {video.filename}</h4>
                      <p className="video-meta">
                        👤 {ownerName} • {formatDate(video.upload_date)}
                        {report && <> • AI: {report.is_abnormal ? 'Abnormal' : 'Normal'} ({report.soundness_score}/100)</>}
                      </p>
                    </div>
                    <span className={`expand-arrow ${isExpanded ? 'expanded' : ''}`}>▸</span>
                  </div>

                  {isExpanded && (
                    <div className="video-expanded label-expanded">
                      {/* Video player */}
                      <div className="label-video-container">
                        <video controls src={getLabeledVideoUrl(video)} className="label-video" ref={el => { if (el) el.playbackRate = 0.5; }} onLoadedMetadata={(e) => { e.target.playbackRate = 0.5; }} />
                      </div>

                      {/* AI's assessment */}
                      {report && (
                        <div className="ai-assessment">
                          <h5>🤖 AI Assessment</h5>
                          <p><strong>{report.is_abnormal ? '⚠️ Abnormal' : '✅ Normal'}</strong> — Score: {report.soundness_score}/100 — Severity: {report.overall_severity}</p>
                          {report.findings?.map((f, i) => (
                            <p key={i} className="ai-finding">• {f.title}: {f.description}</p>
                          ))}
                        </div>
                      )}

                      <BodyPatternTicks videoId={video.id} user={user} />

                      {/* Labeling form */}
                      {form && (
                        <div className="label-form">
                          <h5>🏷️ Your Assessment</h5>

                          <div className="label-question">
                            <p>Is the AI's assessment correct?</p>
                            <div className="label-buttons">
                              <button
                                className={`label-btn ${form.is_correct === true ? 'selected correct' : ''}`}
                                onClick={() => updateLabelField(video.id, 'is_correct', true)}
                              >
                                ✅ Yes, Correct
                              </button>
                              <button
                                className={`label-btn ${form.is_correct === false ? 'selected incorrect' : ''}`}
                                onClick={() => updateLabelField(video.id, 'is_correct', false)}
                              >
                                ❌ No, Incorrect
                              </button>
                            </div>
                          </div>

                          {form.is_correct === false && (
                            <>
                              <div className="label-question">
                                <p>What is the correct result?</p>
                                <div className="label-buttons">
                                  <button
                                    className={`label-btn ${form.correct_result === 'normal' ? 'selected' : ''}`}
                                    onClick={() => updateLabelField(video.id, 'correct_result', 'normal')}
                                  >
                                    Normal
                                  </button>
                                  <button
                                    className={`label-btn ${form.correct_result === 'abnormal' ? 'selected' : ''}`}
                                    onClick={() => updateLabelField(video.id, 'correct_result', 'abnormal')}
                                  >
                                    Abnormal
                                  </button>
                                </div>
                              </div>

                              {form.correct_result === 'abnormal' && (
                                <>
                                  <div className="label-question">
                                    <p>Severity?</p>
                                    <div className="label-buttons">
                                      {['mild', 'moderate', 'severe'].map(s => (
                                        <button
                                          key={s}
                                          className={`label-btn ${form.correct_severity === s ? 'selected' : ''}`}
                                          onClick={() => updateLabelField(video.id, 'correct_severity', s)}
                                        >
                                          {s}
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  <div className="label-question">
                                    <p>Affected limbs? (select all that apply)</p>
                                    <div className="limb-grid">
                                      {LIMB_OPTIONS.map(limb => (
                                        <button
                                          key={limb}
                                          className={`limb-btn ${form.affected_limbs?.includes(limb) ? 'selected' : ''}`}
                                          onClick={() => toggleLimb(video.id, limb)}
                                        >
                                          {limb}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </>
                              )}
                            </>
                          )}

                          <div className="label-question">
                            <p>Notes (what do you see?)</p>
                            <textarea
                              value={form.notes}
                              onChange={(e) => updateLabelField(video.id, 'notes', e.target.value)}
                              placeholder="e.g., Clear right front lameness at trot, shortened stride, head nod..."
                              rows={3}
                            />
                          </div>

                          <button
                            className="save-label-btn"
                            onClick={() => saveLabel(video.id)}
                            disabled={form.is_correct === null || savingLabel === video.id}
                          >
                            {savingLabel === video.id ? 'Saving...' : (labels[video.id] ? '💾 Update Label' : '💾 Save Label')}
                          </button>
                          <div className="video-actions" style={{ marginTop: 12 }}>
                            <button
                              className="action-link delete-btn"
                              onClick={(e) => { e.stopPropagation(); deleteVideo(video); }}
                              disabled={deletingVideo === video.id}
                            >
                              {deletingVideo === video.id ? 'Deleting…' : 'Delete Video'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div className="users-list">
          {users.map(u => (
            <div key={u.id} className="user-card">
              <div className="user-avatar">👤</div>
              <div className="user-info">
                <h4>{u.full_name || 'No name'}</h4>
                <p>{u.email}</p>
                <p className="user-meta">
                  Joined {formatDate(u.created_at)} • {u.subscription_tier || 'free'}
                  {' • '}{videos.filter(v => v.owner_id === u.id).length} videos
                </p>
              </div>
              {u.id !== user.id && (
                <button
                  className={`action-link delete-btn ${confirmDeleteUser === u.id ? 'confirm' : ''}`}
                  onClick={() => deleteUserProfile(u)}
                  disabled={deletingUser === u.id}
                >
                  {deletingUser === u.id
                    ? 'Deleting…'
                    : confirmDeleteUser === u.id
                      ? 'Click again to delete profile'
                      : 'Delete profile'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Training Stats Tab */}
      {tab === 'stats' && (
        <div className="stats-container">
          <div className="stats-grid">
            <div className="stat-box">
              <h3>{videos.length}</h3>
              <p>Total Videos</p>
            </div>
            <div className="stat-box">
              <h3>{labeledCount}</h3>
              <p>Labeled</p>
            </div>
            <div className="stat-box">
              <h3>{videos.length - labeledCount}</h3>
              <p>Unlabeled</p>
            </div>
            <div className="stat-box">
              <h3>{incorrectCount}</h3>
              <p>Corrections</p>
            </div>
          </div>

          <div className="stats-detail">
            <h3>Training Data Progress</h3>
            <div className="progress-bar-container">
              <div className="progress-bar-bg">
                <div className="progress-bar-fill" style={{ width: `${videos.length > 0 ? (labeledCount / videos.length * 100) : 0}%` }} />
              </div>
              <p>{videos.length > 0 ? Math.round(labeledCount / videos.length * 100) : 0}% labeled</p>
            </div>

            <h4>Label Breakdown</h4>
            <ul className="stats-list">
              <li>✅ AI Correct: {labeledCount - incorrectCount}</li>
              <li>❌ AI Incorrect: {incorrectCount}</li>
              <li>⬜ Not yet reviewed: {videos.length - labeledCount}</li>
              {incorrectCount > 0 && (
                <li>📊 AI Accuracy: {labeledCount > 0 ? Math.round((labeledCount - incorrectCount) / labeledCount * 100) : 0}%</li>
              )}
            </ul>

            {incorrectCount > 0 && (
              <>
                <h4>Corrections Detail</h4>
                {Object.values(labels).filter(l => !l.is_correct).map(l => {
                  const v = videos.find(v => v.id === l.video_id);
                  return (
                    <div key={l.id} className="correction-card">
                      <p><strong>{v?.horses?.name || 'Unknown horse'}</strong> — {v?.filename}</p>
                      <p>Should be: <strong>{l.correct_result}</strong> {l.correct_severity && `(${l.correct_severity})`}</p>
                      {l.affected_limbs?.length > 0 && <p>Affected: {l.affected_limbs.join(', ')}</p>}
                      {l.notes && <p className="correction-notes">"{l.notes}"</p>}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
